'use client';

import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { useFloatingListboxPosition } from './use-floating-listbox-position';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  /**
   * RCA-2.14. `compact` sits inside a 32px compact grid row; the default 44px
   * trigger forces the row taller than the density the user asked for.
   */
  size?: 'default' | 'compact';
  error?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
  'aria-label'?: string;
  /**
   * Forwarded onto the inner combobox <button> so callers can associate
   * an external warning/help message with the focused control. Added
   * for W-D (staff-onboarding role→award warning) — non-breaking.
   */
  'aria-describedby'?: string;
}

export function Select({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  className,
  error = false,
  disabled = false,
  name,
  id,
  size = 'default',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();
  const { position } = useFloatingListboxPosition({ isOpen, triggerRef });

  const selectedOption = options.find((o) => o.value === value);

  const close = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (listboxRef.current?.contains(target)) return;
      close();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listboxRef.current) {
      const items = listboxRef.current.querySelectorAll('[role="option"]');
      items[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          const opt = options[highlightedIndex];
          if (opt && !opt.disabled) {
            onChange?.(opt.value);
            close();
          }
        } else {
          setIsOpen(true);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) => {
            let next = prev + 1;
            while (next < options.length && options[next]?.disabled) next++;
            return next < options.length ? next : prev;
          });
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) => {
            let next = prev - 1;
            while (next >= 0 && options[next]?.disabled) next--;
            return next >= 0 ? next : prev;
          });
        }
        break;
      case 'Escape':
        close();
        break;
    }
  };

  const listbox =
    isOpen && position && typeof document !== 'undefined'
      ? createPortal(
          <ul
            id={listboxId}
            ref={listboxRef}
            role="listbox"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
              // A dropdown must render above whatever opened it.
              //
              // Owner, 2026-08-26, on live production: "sitewide that dropdowns
              // are not visible", "I hit the dropdown issue on many surfaces",
              // "a lot of them were on popups such as the log time wizard".
              //
              // This panel portals to document.body with position:fixed, which
              // takes it out of every clipping ancestor - that part was right. But
              // it sat at z-index 80 while dialog.tsx uses z-[200], the create-shift
              // host z-[210] and date-entry z-[220]. So inside ANY modal the panel
              // opened behind the modal: present in the DOM, present in the
              // accessibility tree, invisible on screen. The Log Time wizard is
              // exactly that shape.
              //
              // 300 sits above every overlay in the app. A transient, dismissible
              // popover belongs at the top of the stack by definition - it is not
              // competing with the modal, it is owned by it.
              zIndex: 300,
              transformOrigin: position.placement === 'top' ? 'bottom' : 'top',
            }}
            className="overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-md"
          >
            {options.length === 0 ? (
              <li className="px-4 py-2 text-sm text-slate-500">No options</li>
            ) : (
              options.map((opt, idx) => (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === value}
                  aria-disabled={opt.disabled}
                  onMouseEnter={() => !opt.disabled && setHighlightedIndex(idx)}
                  onClick={() => {
                    if (!opt.disabled) {
                      onChange?.(opt.value);
                      close();
                    }
                  }}
                  className={cn(
                    'flex items-center justify-between px-4 py-2 font-body text-sm cursor-pointer transition-interactive',
                    idx === highlightedIndex && 'bg-mist',
                    opt.value === value && 'text-statura-green-accessible font-medium',
                    opt.disabled && 'cursor-not-allowed opacity-50',
                    !opt.disabled && 'text-text-heading',
                  )}
                >
                  <span className="truncate">{opt.label}</span>
                  {opt.value === value && (
                    <Check className="ml-2 h-4 w-4 shrink-0 text-statura-green-accessible" />
                  )}
                </li>
              ))
            )}
          </ul>,
          document.body,
        )
      : null;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {name && <input type="hidden" name={name} value={value ?? ''} />}
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? listboxId : undefined}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex w-full items-center justify-between border bg-white font-body text-left transition-interactive',
          size === 'compact'
            ? 'h-8 rounded-lg px-2.5 py-1 text-xs'
            : 'h-11 rounded-xl px-4 py-2 text-sm',
          'border-slate-300 hover:border-slate-400',
          'focus:border-statura-green focus:outline-none focus:ring-[3px] focus:ring-statura-green/20',
          error && 'border-red-300 focus:border-red-300 focus:ring-red-100',
          disabled && 'cursor-not-allowed opacity-50',
          selectedOption ? 'text-text-heading' : 'text-text-muted',
        )}
      >
        <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown
          className={cn(
            'ml-2 h-4 w-4 shrink-0 text-slate-400 transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </button>
      {listbox}
    </div>
  );
}
